import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  Stack
} from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { PAGE_CONTENT_MAX_WIDTH_LG } from '~/constants';
import type { OrdersTableActionsProps } from './OrdersTable.types';

export const OrdersTableActions: FC<OrdersTableActionsProps> = ({
  selected,
  setSelected,
  handleApproveItems,
  approveLoading,
  handleRejectItems,
  rejectLoading,
  type
}) => {
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

  const isDisabled = approveLoading || rejectLoading;

  const onApprove = async () => {
    await handleApproveItems(selected);
    setSelected([]);
  };

  const onReject = async () => {
    setIsRejectDialogOpen(false);
    await handleRejectItems(selected);
    setSelected([]);
  };

  const onCancel = () => setSelected([]);

  const handleOpenRejectDialog = () => setIsRejectDialogOpen(true);
  const handleCloseRejectDialog = () => setIsRejectDialogOpen(false);

  return (
    <Fade in={selected.length > 0}>
      <Box
        position="fixed"
        bottom={0}
        right={0}
        zIndex={1}
        width="100%"
        bgcolor="background.paper"
        boxShadow="0px -4px 9px 0px #0000000D"
        py={{ xs: 2.5 }}
      >
        <Box maxWidth={PAGE_CONTENT_MAX_WIDTH_LG} mx="auto" px={{ xs: 3.25, lg: 6 }}>
          <Stack direction="row" spacing={2.5} justifyContent="flex-end">
            {/* Cancel */}
            <Button
              id={`${type}_orders_table_actions_cancel_button`}
              size="large"
              color="primary"
              variant="text"
              disabled={isDisabled}
              onClick={onCancel}
            >
              Cancel
            </Button>

            {/* Reject */}
            <Button
              id={`${type}_orders_table_actions_reject_button`}
              size="large"
              color="error"
              variant="outlined"
              disabled={isDisabled}
              onClick={handleOpenRejectDialog}
            >
              {rejectLoading ? 'Rejecting...' : 'Reject'}
            </Button>
            <Dialog
              open={isRejectDialogOpen}
              onClose={handleCloseRejectDialog}
              aria-labelledby="reject-dialog"
              aria-describedby="reject-dialog-description"
            >
              <DialogTitle id="reject-dialog">Reject Product</DialogTitle>

              <DialogContent>
                <DialogContentText id="reject-dialog-description">
                  Are you sure you want to reject the selected product(s)?
                </DialogContentText>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleCloseRejectDialog} variant="outlined" color="primary">
                  Cancel
                </Button>
                <Button onClick={onReject} variant="contained" color="error" autoFocus>
                  Reject
                </Button>
              </DialogActions>
            </Dialog>

            {/* Approve */}
            <Button
              id={`${type}_orders_table_actions_approve_button`}
              size="large"
              color="primary"
              variant="contained"
              disabled={isDisabled}
              onClick={onApprove}
            >
              {approveLoading ? 'Approving...' : 'Approve'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Fade>
  );
};
